import { Button, Space, Skeleton, InputNumber, Modal, Select, TimePicker } from "antd";

import React, { useEffect, useState, useMemo } from "react";
// React 17 only batches inside event handlers; this is the escape hatch for async code.
import { unstable_batchedUpdates as batchUpdates } from "react-dom";

import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useToast } from "../../../../contexts/ToastContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import apiClient from "../../../../helpers/apiClient";

import PreferencesSwitch from "./PreferencesSwitch";
import BufferTimeSelector, { BUFFER_TIME_OPTIONS } from "./BufferTimeSelector";
import SettingsPageLayout from "../SettingsPageLayout";
import SettingsEditLink from "../SettingsEditLink";
import {
    operationalHoursToPickerValue,
    pickerValueToOperationalHours,
    buildTimeZoneSelectOptions,
    resolveCompanyTimeZone,
    timeZoneAbbreviation,
} from "../../../../helpers/operationalHours";

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = TimePicker;

/** @param {unknown} raw */
const normalizeBufferTimeMinutes = (raw) => {
    const parsed =
        typeof raw === "number" && !Number.isNaN(raw)
            ? raw
            : raw != null
              ? Number.parseInt(String(raw), 10)
              : NaN;

    if (!Number.isFinite(parsed)) {
        return 60;
    }

    if (BUFFER_TIME_OPTIONS.includes(parsed)) {
        return parsed;
    }

    return BUFFER_TIME_OPTIONS.reduce((closest, option) =>
        Math.abs(option - parsed) < Math.abs(closest - parsed) ? option : closest,
    );
};

const PREFERENCE_ROW_CLASS =
    "w-full rounded-2xl border border-[#ECECF3] bg-white px-4 py-4 flex flex-col gap-4 lg:min-h-28 lg:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-5";
const PREFERENCE_TEXT_CLASS = "min-w-0 flex-1";
const PREFERENCE_SWITCH_WRAP_CLASS =
    "flex w-full shrink-0 items-center justify-start lg:w-[150px] lg:justify-end";
const PREFERENCE_EXTRA_CONTROLS_CLASS =
    "flex w-full flex-col gap-2 pt-1 lg:w-auto lg:flex-row lg:items-center lg:gap-4 lg:pt-0";

const Preferences = ({ companyData, initialLoading, fetchCompanyData }) => {
    const { t } = useTranslation("settings");
    const { updateMyCompanyData, fetchCompanyData: refreshGlobalCompanyData } = useUser();
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // State variables for preferences
    const [overbooking, setOverbooking] = useState(companyData?.preferences?.overbooking || false);
    const [selfReservationManagementStatus, setSelfReservationManagementStatus] = useState(false);
    const [multipleSignatureStatus, setMultipleSignatureStatus] = useState(false);
    const [showMinAgeToRent, setShowMinAgeToRent] = useState(false);
    const [showInsurance, setShowInsurance] = useState(false);
    const [showDrivingLicenseVerification, setShowDrivingLicenseVerification] = useState(false);
    const [showOperationalHours, setShowOperationalHours] = useState(false);
    const [driverLicenseRequiredToCreateReservation, setDriverLicenseRequiredToCreateReservation] = useState(false);
    const [insuranceRequiredToCreateReservation, setInsuranceRequiredToCreateReservation] = useState(false);
    const [pricingMode, setPricingMode] = useState(companyData?.preferences?.pricingMode || "car");
    // "auto" = booking site's Featured row shows available cars; "manual" = only cars marked featured.
    const [featuredCarsAuto, setFeaturedCarsAuto] = useState(companyData?.preferences?.featuredCarsMode !== "manual");

    // value states
    const [minimumAge, setMinimumAge] = useState(18);
    const [operationalHours, setOperationalHours] = useState(null);
    // The company's own clock. "" means "derive it from the company address".
    const [companyTimeZone, setCompanyTimeZone] = useState(companyData?.timezone || "");
    const [bufferTimeMinutes, setBufferTimeMinutes] = useState(60);
    const [enableBufferTime, setEnableBufferTime] = useState(false);
    const [extendReservationByDay, setExtendReservationByDay] = useState(
        companyData?.preferences?.extendReservationByDay === true,
    );
    const toast = useToast();

    // Which clock the operational-hours window is expressed in. Resolved from the company's
    // explicit `timezone`, falling back to its address — never the admin's own browser.
    const companyTimeZoneLabel = useMemo(() => {
        const zone = resolveCompanyTimeZone({ ...companyData, timezone: companyTimeZone || undefined });
        const abbreviation = timeZoneAbbreviation(zone);
        return abbreviation ? `${zone} (${abbreviation})` : zone;
    }, [companyData, companyTimeZone]);

    // What "" resolves to, so the "from address" option names the zone it will actually use.
    const addressTimeZone = useMemo(
        () => resolveCompanyTimeZone({ ...companyData, timezone: undefined }),
        [companyData],
    );
    const companyTimeZoneOptions = useMemo(
        () => [
            { value: "", label: t("preferences.companyTimezoneFromAddress", { zone: addressTimeZone }) },
            ...buildTimeZoneSelectOptions(companyTimeZone, {
                us: t("preferences.timezoneGroupUs"),
                other: t("preferences.timezoneGroupOther"),
            }),
        ],
        [addressTimeZone, companyTimeZone, t],
    );

    // Check if defaultPaymentGateway is set
    const isDefaultPaymentGatewaySet = useMemo(
        () => !!companyData?.defaultPaymentGateway,
        [companyData?.defaultPaymentGateway],
    );

    // Update local state when company data changes
    useEffect(() => {
        setOverbooking(companyData?.preferences?.overbooking || false);

        // Check if defaultPaymentGateway is set - if not, force disable these features
        const hasDefaultPaymentGateway = !!companyData?.defaultPaymentGateway;
        setSelfReservationManagementStatus(
            hasDefaultPaymentGateway ? companyData?.preferences?.selfReservationManagement || false : false,
        );

        // Check if insurance is connected
        const hasInsuranceConnected = companyData?.insurance?.some((ins) => ins.enabled === true);

        // If no insurance connected or no defaultPaymentGateway, force showInsurance to false, otherwise use preference value
        setShowInsurance(
            hasDefaultPaymentGateway && hasInsuranceConnected
                ? companyData?.preferences?.showInsurance || false
                : false,
        );

        setShowDrivingLicenseVerification(companyData?.preferences?.showDrivingLicenseVerification || false);
        setMultipleSignatureStatus(companyData?.preferences?.multipleSignatures || false);
        setMinimumAge(companyData?.minAgeToRent || 18); // Default to 18 if not set
        setShowMinAgeToRent(companyData?.preferences?.showMinAgeToRent || false);
        setShowOperationalHours(companyData?.preferences?.enableOperationalHours || false);
        // Operational hours are a time-of-day in the company's own timezone, not an instant.
        // `operationalHoursToPickerValue` reads whichever format is stored (new `startMinutes` or
        // the legacy Date) without any timezone conversion, so the digits an admin sees are the
        // digits the business set, wherever that admin happens to be.
        setOperationalHours(operationalHoursToPickerValue(companyData?.operationalHours));
        setCompanyTimeZone(companyData?.timezone || "");
        setDriverLicenseRequiredToCreateReservation(
            companyData?.preferences?.driverLicenseRequiredToCreateReservation || false,
        );
        setInsuranceRequiredToCreateReservation(
            companyData?.preferences?.insuranceRequiredToCreateReservation || false,
        );
        const minutesSource =
            companyData?.bufferTimeMinutes != null
                ? companyData.bufferTimeMinutes
                : companyData?.preferences?.bufferTime;
        setBufferTimeMinutes(normalizeBufferTimeMinutes(minutesSource));
        setEnableBufferTime(companyData?.preferences?.enableBufferTime === true);
        setExtendReservationByDay(companyData?.preferences?.extendReservationByDay === true);
        setPricingMode(companyData?.preferences?.pricingMode === "category" ? "category" : "car");
        setFeaturedCarsAuto(companyData?.preferences?.featuredCarsMode !== "manual");
    }, [companyData]);

    // Automatically disable showInsurance in database if no insurance is connected
    useEffect(() => {
        const hasInsuranceConnected = companyData?.insurance?.some((ins) => ins.enabled === true);

        // If no insurance connected but showInsurance is enabled in database, update database
        if (!hasInsuranceConnected && companyData?.preferences?.showInsurance === true) {
            const updateDatabase = async () => {
                try {
                    await apiClient.patch("/api/company/myCompany", {
                        preferences: {
                            ...companyData.preferences,
                            showInsurance: false,
                            insuranceRequiredToCreateReservation: false,
                        },
                    });

                    // Refresh company data after update
                    if (fetchCompanyData) {
                        await fetchCompanyData();
                    }
                } catch (_error) {
                    // Silently handle error for auto-disable
                }
            };

            updateDatabase();
        }
    }, [companyData?.insurance, companyData?.preferences?.showInsurance, fetchCompanyData, companyData?.preferences]);

    // Automatically disable features in database if defaultPaymentGateway is not set
    useEffect(() => {
        const hasDefaultPaymentGateway = !!companyData?.defaultPaymentGateway;

        // If defaultPaymentGateway is not set but features are enabled in database, update database
        if (!hasDefaultPaymentGateway && companyData?.preferences) {
            const needsUpdate =
                companyData.preferences.selfReservationManagement === true ||
                companyData.preferences.showInsurance === true;

            if (needsUpdate) {
                const updateDatabase = async () => {
                    try {
                        await apiClient.patch("/api/company/myCompany", {
                            preferences: {
                                ...companyData.preferences,
                                selfReservationManagement: false,
                                showInsurance: false,
                                insuranceRequiredToCreateReservation: false,
                            },
                        });

                        // Refresh company data after update
                        if (fetchCompanyData) {
                            await fetchCompanyData();
                        }
                    } catch (_error) {
                        // Silently handle error for auto-disable
                    }
                };

                updateDatabase();
            }
        }
    }, [companyData?.defaultPaymentGateway, companyData?.preferences, fetchCompanyData]);

    // Keep "full payment required" enforced in the API while the UI is locked to always-on
    useEffect(() => {
        if (companyData?.preferences?.fullPaymentRequiredToCreateReservation === true) return;
        if (!companyData?.preferences) return;
        const sync = async () => {
            try {
                await apiClient.patch("/api/company/myCompany", {
                    preferences: {
                        ...companyData.preferences,
                        fullPaymentRequiredToCreateReservation: true,
                    },
                });
                if (fetchCompanyData) {
                    await fetchCompanyData();
                }
            } catch (_error) {
                // best-effort; user can still save from preferences
            }
        };
        sync();
    }, [companyData, fetchCompanyData]);

    // Force-disable local state when defaultPaymentGateway is not set
    useEffect(() => {
        if (!isDefaultPaymentGatewaySet) {
            setSelfReservationManagementStatus((prev) => (prev ? false : prev));
            setShowInsurance((prev) => {
                if (prev) {
                    setInsuranceRequiredToCreateReservation(false);
                    return false;
                }
                return prev;
            });
        }
    }, [isDefaultPaymentGatewaySet]);

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            let operationalHoursPayload = null;
            if (
                showOperationalHours &&
                operationalHours &&
                Array.isArray(operationalHours) &&
                operationalHours.length === 2
            ) {
                // Save the window as minutes past midnight in the company's timezone. Storing it
                // in a Date forced a instant onto a value that has no date and no offset, which is
                // what made "9 AM" mean something different to every reader.
                operationalHoursPayload = pickerValueToOperationalHours(operationalHours);
            }

            // Saving the toggle as "on" with no window would omit `operationalHours` from the
            // PATCH, leaving whatever window was stored before still in force on the booking site
            // while this page shows an empty picker. The operator reads that as the toggle being
            // broken. Block the save instead, the same way the onboarding step does.
            if (showOperationalHours && !operationalHoursPayload) {
                toast.error({
                    message: t("preferences.operationalHoursRequired"),
                });
                return;
            }

            // A window whose ends are the same minute leaves exactly one bookable minute, so the
            // API rejects it. Say why here rather than letting it come back as a generic 422.
            if (
                operationalHoursPayload &&
                operationalHoursPayload.startMinutes === operationalHoursPayload.endMinutes
            ) {
                toast.error({
                    message: t("preferences.operationalHoursSameTime"),
                });
                return;
            }

            const clampedBufferTimeMinutes = normalizeBufferTimeMinutes(bufferTimeMinutes);

            const preferencesPayload = {
                overbooking,
                selfReservationManagement: selfReservationManagementStatus,
                multipleSignatures: multipleSignatureStatus,
                showMinAgeToRent,
                showPreDepositOption: false,
                showInsurance,
                showDrivingLicenseVerification,
                driverLicenseRequiredToCreateReservation,
                insuranceRequiredToCreateReservation,
                fullPaymentRequiredToCreateReservation: true,
                enableOperationalHours: showOperationalHours,
                enableBufferTime,
                extendReservationByDay,
                pricingMode,
                featuredCarsMode: featuredCarsAuto ? "auto" : "manual",
            };

            const response = await apiClient.patch("/api/company/myCompany", {
                // null clears it, so the zone is derived from the company address again.
                timezone: companyTimeZone || null,
                ...(operationalHoursPayload && { operationalHours: operationalHoursPayload }),
                preferences: preferencesPayload,
                bufferTimeMinutes: clampedBufferTimeMinutes,
                minAgeToRent: minimumAge,
                preDepositAmount: 1,
            });

            if (response.data.success) {
                toast.success({
                    message: t("successfullyUpdatedInformation"),
                    description: "Your preferences have been saved",
                });

                // Patch the context with what we just saved and leave edit mode together, so the
                // page settles on its final layout in one paint.
                //
                // React 17 has no automatic batching outside event handlers, and everything here
                // runs after an `await`, so each setState would otherwise render on its own: the
                // new values would paint into the still-editable controls, then the controls
                // would swap to read-only text a frame later.
                batchUpdates(() => {
                    updateMyCompanyData({
                        timezone: companyTimeZone || null,
                        preferences: preferencesPayload,
                        bufferTimeMinutes: clampedBufferTimeMinutes,
                        minAgeToRent: minimumAge,
                        ...(operationalHoursPayload && { operationalHours: operationalHoursPayload }),
                    });
                    setIsEditing(false);
                    setIsSubmitting(false);
                });

                // One reconciling refetch, not two. `fetchCompanyData` is the parent's wrapper
                // around this very same context loader, so awaiting both ran the request twice
                // and re-seeded every field twice for one save. Either path repins the
                // dashboard's clock via `setActiveCompanyTimeZone`.
                await (fetchCompanyData ?? refreshGlobalCompanyData)();
            } else {
                throw new Error("Failed to update preferences");
            }
        } catch (error) {
            const data = error?.response?.data;
            const detail =
                data?.errorSources?.map((source) => source?.message).filter(Boolean).join(" ") ||
                (data?.message && data.message !== "Validation Error" ? data.message : "") ||
                t("pleaseTryAgainLater");

            toast.error({
                message: t("failedToUpdateInformation"),
                description: detail,
            });
        } finally {
            // Covers the guard and error paths; the success path already cleared it, batched
            // with the rest, and setting the same value again does not re-render.
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setOverbooking(companyData?.preferences?.overbooking || false);
        setPricingMode(companyData?.preferences?.pricingMode === "category" ? "category" : "car");
        setFeaturedCarsAuto(companyData?.preferences?.featuredCarsMode !== "manual");
        const cancelMinutesSource =
            companyData?.bufferTimeMinutes != null
                ? companyData.bufferTimeMinutes
                : companyData?.preferences?.bufferTime;
        setBufferTimeMinutes(normalizeBufferTimeMinutes(cancelMinutesSource));
        setEnableBufferTime(companyData?.preferences?.enableBufferTime === true);
        setExtendReservationByDay(companyData?.preferences?.extendReservationByDay === true);
        setShowOperationalHours(companyData?.preferences?.enableOperationalHours || false);
        setOperationalHours(operationalHoursToPickerValue(companyData?.operationalHours));
        setCompanyTimeZone(companyData?.timezone || "");
        setIsEditing(false);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSelfReservationManagementChange = (value) => {
        if (value && !isDefaultPaymentGatewaySet) {
            Modal.warning({
                title: t("preferences.modal.warning.warning"),
                content: t("preferences.modal.warning.stripeRequiredForSelfReservation"),
                okText: t("close"),
                centered: true,
            });
            return;
        }
        setSelfReservationManagementStatus(value);
    };

    const handleMinAgeToggleChange = (value) => {
        setShowMinAgeToRent(value);
        if (value && (!minimumAge || minimumAge <= 0)) {
            setMinimumAge(18);
        }
    };

    const handleMinimumAgeChange = (value) => {
        if (value === null || value === undefined || value === "") {
            return;
        }
        if (typeof value === "number" && value > 0) {
            setMinimumAge(value);
        }
    };

    const handleBufferTimeChange = (value) => {
        if (BUFFER_TIME_OPTIONS.includes(value)) {
            setBufferTimeMinutes(value);
        }
    };

    const handleBufferTimeToggleChange = (value) => {
        setEnableBufferTime(value);
        if (value && !BUFFER_TIME_OPTIONS.includes(bufferTimeMinutes)) {
            setBufferTimeMinutes(60);
        }
    };

    const handleOperationalHoursToggleChange = (value) => {
        setShowOperationalHours(value);
        if (value && !operationalHours) {
            // Set default operational hours if none exist (9 AM - 5 PM)
            const defaultStart = dayjs().hour(9).minute(0).second(0);
            const defaultEnd = dayjs().hour(17).minute(0).second(0);
            setOperationalHours([defaultStart, defaultEnd]);
        } else if (!value) {
            setOperationalHours(null);
        }
    };

    const handleOperationalHoursChange = (value) => {
        setOperationalHours(value);
    };

    const handleInsuranceRequirementChange = (value) => {
        if (value && (!showInsurance || !showDrivingLicenseVerification)) {
            Modal.confirm({
                title: t("preferences.modal.warning.warning"),
                content: t("preferences.modal.warning.insuranceRequiredEnableConflict"),
                okText: t("yes"),
                cancelText: t("no"),
                onOk: () => {
                    setInsuranceRequiredToCreateReservation(value);
                    setShowInsurance(true);
                    setShowDrivingLicenseVerification(true);
                },
                centered: true,
            });
        } else {
            setInsuranceRequiredToCreateReservation(value);
        }
    };

    const handleInsuranceMangementChange = (value) => {
        // Check if defaultPaymentGateway is set when trying to enable
        if (value && !isDefaultPaymentGatewaySet) {
            Modal.warning({
                title: t("preferences.modal.warning.warning"),
                content: t("preferences.modal.warning.stripeRequiredForInsurance"),
                okText: t("close"),
                centered: true,
            });
            return;
        }

        // Check if insurance is connected when trying to enable
        if (value) {
            const hasInsuranceConnected = companyData?.insurance?.some((ins) => ins.enabled === true);

            if (!hasInsuranceConnected) {
                Modal.warning({
                    title: t("preferences.modal.warning.warning"),
                    content: t("preferences.modal.warning.insuranceNotConnected"),
                    okText: t("close"),
                    centered: true,
                });
                return;
            }
        }

        if (value && !showDrivingLicenseVerification) {
            Modal.confirm({
                title: t("preferences.modal.warning.warning"),
                content: t("preferences.modal.warning.insuranceEnableDrivingLicenseConflict"),
                okText: t("yes"),
                cancelText: t("no"),
                onOk: () => {
                    setShowInsurance(value);
                    setShowDrivingLicenseVerification(true);
                },
                centered: true,
            });
        } else if (!value && insuranceRequiredToCreateReservation) {
            Modal.confirm({
                title: t("preferences.modal.warning.warning"),
                content: t("preferences.modal.warning.insuranceDisableConflict"),
                okText: t("yes"),
                cancelText: t("no"),
                onOk: () => {
                    setShowInsurance(value);
                    setInsuranceRequiredToCreateReservation(false);
                },
                centered: true,
            });
        } else {
            setShowInsurance(value);
        }
    };

    const handleDriverLicenseRequirementChange = (value) => {
        if (value && !showDrivingLicenseVerification) {
            Modal.confirm({
                title: t("preferences.modal.warning.warning"),
                content: t("preferences.modal.warning.driverLicenseEnableConflict"),
                okText: t("yes"),
                cancelText: t("no"),
                onOk: () => {
                    setDriverLicenseRequiredToCreateReservation(value);
                    setShowDrivingLicenseVerification(true);
                },
                centered: true,
            });
        } else {
            setDriverLicenseRequiredToCreateReservation(value);
        }
    };

    // const handlePricingModeChange = (value) => {
    //     const newMode = value ? "car" : "category";
    //     if (newMode === pricingMode) return;
    //
    //     Modal.confirm({
    //         title: t("preferences.modal.warning.warning"),
    //         content: t("preferences.pricingModeConfirm"),
    //         okText: t("yes"),
    //         cancelText: t("no"),
    //         onOk: () => setPricingMode(newMode),
    //         centered: true,
    //     });
    // };

    const handleDrivingLicenseVerificationChange = (value) => {
        if (!value && (driverLicenseRequiredToCreateReservation || showInsurance)) {
            Modal.confirm({
                title: t("preferences.modal.warning.warning"),
                content: t("preferences.modal.warning.drivingLicenseVerificationDisableConflict"),
                okText: t("yes"),
                cancelText: t("no"),
                onOk: () => {
                    setShowDrivingLicenseVerification(value);
                    setDriverLicenseRequiredToCreateReservation(false);
                    setShowInsurance(false);
                    setInsuranceRequiredToCreateReservation(false);
                },
                centered: true,
            });
        } else {
            setShowDrivingLicenseVerification(value);
        }
    };

    return (
        <SettingsPageLayout
            className="overflow-auto"
            title={t("preferences.preferences")}
            description={t("preferences.description")}
            actions={
                initialLoading ? (
                    <Skeleton.Button style={{ width: "132px" }} />
                ) : isEditing ? (
                    <Space>
                        <Button shape="round" onClick={handleCancel} disabled={isSubmitting}>
                            {t("cancel")}
                        </Button>
                        <Button
                            loading={isSubmitting}
                            type="primary"
                            disabled={isSubmitting}
                            onClick={handleSave}
                            htmlType="submit"
                            shape="round">
                            {isSubmitting ? t("saving") : t("save")}
                        </Button>
                    </Space>
                ) : (
                    <SettingsEditLink onClick={handleEdit} />
                )
            }>
                {initialLoading ? (
                    <div className="flex flex-col gap-3">
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                        <Skeleton.Node fullSize active style={{ width: "100%", height: "80px", borderRadius: "16px" }} />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {/* Overbooking Setting */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.overbooking")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.overbookingDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={overbooking}
                                    setState={setOverbooking}
                                />
                            </div>
                        </div>
                        {/* Per-car pricing mode */}
                        {/* <div className="w-full min-h-28 px-6 py-4 flex justify-between items-center bg-white rounded-2xl gap-5">
                            <div className="flex-1">
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.pricingMode")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.pricingModeDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={pricingMode === "car"}
                                    setState={handlePricingModeChange}
                                />
                            </div>
                        </div> */}
                        {/* Buffer time (minutes) */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={`${PREFERENCE_TEXT_CLASS} flex flex-col gap-4`}>
                                <div className="min-w-0">
                                    <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                        {t("preferences.bufferTime")}
                                    </h5>
                                    <p className="text-[14px] leading-relaxed text-[#787882]">
                                        {t("preferences.bufferTimeDescription")}
                                    </p>
                                </div>
                                <div className={PREFERENCE_EXTRA_CONTROLS_CLASS}>
                                    <label className="text-[14px] text-[#4B4B5C]" htmlFor="preferences-buffer-time">
                                        {t("preferences.bufferTimeLabel")}
                                    </label>
                                    <BufferTimeSelector
                                        id="preferences-buffer-time"
                                        value={bufferTimeMinutes}
                                        onChange={handleBufferTimeChange}
                                        disabled={!isEditing || !enableBufferTime}
                                    />
                                </div>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={enableBufferTime}
                                    setState={handleBufferTimeToggleChange}
                                />
                            </div>
                        </div>
                        {/* Featured cars on the booking site */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.featuredCarsAuto")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.featuredCarsAutoDescription")}
                                </p>
                                {!featuredCarsAuto && (
                                    <p className="mt-2 text-[13px] text-[#5D5DF6]">
                                        {t("preferences.featuredCarsManualHint")}
                                    </p>
                                )}
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={featuredCarsAuto}
                                    setState={setFeaturedCarsAuto}
                                />
                            </div>
                        </div>
                        {/* Extend reservation by day */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.extendReservationByDay")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.extendReservationByDayDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={extendReservationByDay}
                                    setState={setExtendReservationByDay}
                                />
                            </div>
                        </div>
                        {/* Self Reservation Management Setting */}
                        <div
                            className={`${PREFERENCE_ROW_CLASS} ${!isDefaultPaymentGatewaySet ? "opacity-60" : ""}`}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.selfResManagement")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.selfResManagementDescription")}
                                </p>
                                {!isDefaultPaymentGatewaySet && (
                                    <p className="mt-2 text-[13px] text-red-600">{t("preferences.stripeRequiredNote")}</p>
                                )}
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={selfReservationManagementStatus}
                                    setState={handleSelfReservationManagementChange}
                                    disabled={!isDefaultPaymentGatewaySet && isEditing}
                                    disabledTooltip={
                                        !isDefaultPaymentGatewaySet ? t("preferences.stripeRequiredTooltip") : null
                                    }
                                />
                            </div>
                        </div>
                        {/* Driving License Verification Setting */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.drivingLicenseVerification")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.drivingLicenseVerificationDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={showDrivingLicenseVerification}
                                    setState={handleDrivingLicenseVerificationChange}
                                />
                            </div>
                        </div>
                        {/* Driver License Required to Create Reservation Management Setting */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.driverLicenseRequired")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.driverLicenseRequiredDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={driverLicenseRequiredToCreateReservation}
                                    setState={handleDriverLicenseRequirementChange}
                                />
                            </div>
                        </div>
                        {/* Insurance Management Setting */}
                        <div
                            className={`${PREFERENCE_ROW_CLASS} ${!isDefaultPaymentGatewaySet ? "opacity-60" : ""}`}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.insuranceManagement")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.insuranceManagementDescription")}
                                </p>
                                {!isDefaultPaymentGatewaySet && (
                                    <p className="mt-2 text-[13px] text-red-600">{t("preferences.stripeRequiredNote")}</p>
                                )}
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={showInsurance}
                                    setState={handleInsuranceMangementChange}
                                    disabled={!isDefaultPaymentGatewaySet && isEditing}
                                    disabledTooltip={
                                        !isDefaultPaymentGatewaySet ? t("preferences.stripeRequiredTooltip") : null
                                    }
                                />
                            </div>
                        </div>
                        {/* Insurance Required to Create Reservation Management Setting */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.insuranceRequiredToCreateReservation")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.insuranceRequiredToCreateReservationDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={insuranceRequiredToCreateReservation}
                                    setState={handleInsuranceRequirementChange}
                                />
                            </div>
                        </div>
                        {/* Multiple signature Management Setting
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.multipleSignature")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.multipleSignatureDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={multipleSignatureStatus}
                                    setState={setMultipleSignatureStatus}
                                />
                            </div>
                        </div> */}
                        {/* Age management Setting */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={`${PREFERENCE_TEXT_CLASS} flex flex-col gap-4`}>
                                <div className="min-w-0">
                                    <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                        {t("preferences.ageManagement")}
                                    </h5>
                                    <p className="text-[14px] leading-relaxed text-[#787882]">
                                        {t("preferences.ageManagementDescription")}
                                    </p>
                                </div>
                                <div className={PREFERENCE_EXTRA_CONTROLS_CLASS}>
                                    <label className="text-[14px] text-[#4B4B5C]">{t("preferences.ageLabel")}</label>
                                    <InputNumber
                                        min={1}
                                        value={minimumAge}
                                        onChange={handleMinimumAgeChange}
                                        disabled={!isEditing || !showMinAgeToRent}
                                        size="small"
                                        className="w-full max-w-[8rem]"
                                        parser={(val) => (val ? val.replace(/\s/g, "") : val)}
                                    />
                                </div>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={showMinAgeToRent}
                                    setState={handleMinAgeToggleChange}
                                />
                            </div>
                        </div>
                        {/* Company timezone — the clock operational hours are written on and the
                            fallback for any location without its own timezone. */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.companyTimezone")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.companyTimezoneDescription")}
                                </p>
                            </div>
                            <div className="flex w-full shrink-0 flex-col items-stretch lg:w-[320px] lg:items-end">
                                {isEditing ? (
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        value={companyTimeZone}
                                        onChange={setCompanyTimeZone}
                                        options={companyTimeZoneOptions}
                                        className="w-full"
                                        popupMatchSelectWidth={false}
                                        getPopupContainer={() => document.body}
                                    />
                                ) : (
                                    <span className="break-words text-[14px] text-[#4B4B5C]">{companyTimeZoneLabel}</span>
                                )}
                            </div>
                        </div>
                        {/* Operational Hours Setting */}
                        <div data-tour="preferences-operational-hours" className={PREFERENCE_ROW_CLASS}>
                            <div className={`${PREFERENCE_TEXT_CLASS} flex flex-col gap-4`}>
                                <div className="min-w-0">
                                    <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                        {t("preferences.operationalHours")}
                                    </h5>
                                    <p className="text-[14px] leading-relaxed text-[#787882]">
                                        {t("preferences.operationalHoursDescription")}
                                    </p>
                                    <p className="mt-1 text-[13px] text-[#9b9ba5]">
                                        {t("preferences.operationalHoursLocationNote")}
                                    </p>
                                </div>
                                <div className="flex w-full flex-col flex-wrap gap-2 sm:flex-row sm:items-center sm:gap-4">
                                    <RangePicker
                                        format="hh:mm A"
                                        value={operationalHours}
                                        onChange={handleOperationalHoursChange}
                                        disabled={!isEditing || !showOperationalHours}
                                        minuteStep={30}
                                        size="small"
                                        changeOnScroll={true}
                                        className="w-full [&_.ant-picker]:w-full sm:w-auto"
                                        getPopupContainer={() => document.body}
                                    />
                                    {companyTimeZoneLabel ? (
                                        <span className="text-[13px] text-[#9b9ba5] sm:whitespace-nowrap">
                                            {companyTimeZoneLabel}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state={showOperationalHours}
                                    setState={handleOperationalHoursToggleChange}
                                />
                            </div>
                        </div>
                        {/* Pre-deposit management UI commented out; API saves showPreDepositOption: false and preDepositAmount: null. */}
                        {/* Full payment to create reservation — always enabled; switch is display-only. */}
                        <div className={PREFERENCE_ROW_CLASS}>
                            <div className={PREFERENCE_TEXT_CLASS}>
                                <h5 className="mb-1 text-[15px] font-semibold text-[#2F2F46]">
                                    {t("preferences.fullPaymentRequiredToCreateReservation")}
                                </h5>
                                <p className="text-[14px] leading-relaxed text-[#787882]">
                                    {t("preferences.fullPaymentRequiredToCreateReservationDescription")}
                                </p>
                            </div>
                            <div className={PREFERENCE_SWITCH_WRAP_CLASS}>
                                <PreferencesSwitch
                                    isEditing={isEditing}
                                    state
                                    setState={() => {}}
                                    disabled
                                    disabledTooltip={t("preferences.fullPaymentRequiredMandatoryTooltip")}
                                />
                            </div>
                        </div>
                    </div>
                )}
        </SettingsPageLayout>
    );
};

Preferences.propTypes = {
    companyData: PropTypes.object,
    initialLoading: PropTypes.bool,
    fetchCompanyData: PropTypes.func,
};

export default Preferences;
